import { supabase } from '../lib/supabase';
import { getCurrentSchoolYear } from './schoolYear';

interface AutoAllocationOptions {
  userId: string;
  transactionId: string;
  categoryId: string;
  amount: number;
  fiscalYearStart?: { month: number; day: number };
}

export interface AutoAllocationResult {
  success: boolean;
  message?: string;
  reason?: 'already_allocated' | 'no_budget_line' | 'error';
}

export async function autoAllocateToBudget(options: AutoAllocationOptions): Promise<AutoAllocationResult> {
  const { userId, transactionId, categoryId, amount, fiscalYearStart } = options;

  try {
    // Get fiscal year (default to September 1st if not provided)
    const startMonth = fiscalYearStart?.month ?? 9;
    const startDay = fiscalYearStart?.day ?? 1;
    const currentYear = getCurrentSchoolYear(startMonth, startDay);

    console.log('[Auto-Allocation] Starting for transaction:', transactionId);
    console.log('[Auto-Allocation] Category:', categoryId, 'Fiscal year:', currentYear);

    // Find matching budget line for the current fiscal year
    const { data: budgetLine, error: budgetError } = await supabase
      .from('budget_lines')
      .select('id, planned_amount, categories(name)')
      .eq('user_id', userId)
      .eq('category_id', categoryId)
      .eq('year', currentYear)
      .maybeSingle();

    if (budgetError) {
      console.error('[Auto-Allocation] Error fetching budget line:', budgetError);
      return {
        success: false,
        reason: 'error',
        message: `Erreur lors de la recherche de ligne budgétaire: ${budgetError.message}`
      };
    }

    if (!budgetLine) {
      console.log('[Auto-Allocation] No matching budget line found');
      return {
        success: false,
        reason: 'no_budget_line',
        message: `Aucune ligne budgétaire trouvée pour cette catégorie sur l'exercice ${currentYear}. Créez d'abord une ligne budgétaire dans l'onglet "Budget prévisionnel".`
      };
    }

    // Check if transaction is already allocated
    const { data: existingAllocation } = await supabase
      .from('budget_allocations')
      .select('id, budget_line_id, allocated_amount')
      .eq('transaction_id', transactionId)
      .maybeSingle();

    if (existingAllocation) {
      // If allocation exists but with different line or amount, update it
      if (existingAllocation.budget_line_id !== budgetLine.id ||
          Number(existingAllocation.allocated_amount) !== Math.abs(amount)) {
        console.log('[Auto-Allocation] Updating existing allocation');
        const { error: updateError } = await supabase
          .from('budget_allocations')
          .update({
            budget_line_id: budgetLine.id,
            allocated_amount: Math.abs(amount)
          })
          .eq('id', existingAllocation.id);

        if (updateError) {
          console.error('[Auto-Allocation] Error updating allocation:', updateError);
          return {
            success: false,
            reason: 'error',
            message: `Erreur lors de la mise à jour de l'imputation: ${updateError.message}`
          };
        }

        return {
          success: true,
          message: 'Imputation mise à jour automatiquement'
        };
      }

      console.log('[Auto-Allocation] Transaction already allocated with correct values');
      return { success: true, reason: 'already_allocated' };
    }

    // Create automatic allocation
    const { error: allocationError } = await supabase
      .from('budget_allocations')
      .insert({
        budget_line_id: budgetLine.id,
        transaction_id: transactionId,
        allocated_amount: Math.abs(amount)
      });

    if (allocationError) {
      console.error('[Auto-Allocation] Error creating allocation:', allocationError);
      return {
        success: false,
        reason: 'error',
        message: `Erreur lors de l'imputation: ${allocationError.message}`
      };
    }

    console.log('[Auto-Allocation] Success! Allocated to budget line:', budgetLine.id);
    return {
      success: true,
      message: `Imputée automatiquement au budget`
    };
  } catch (err: any) {
    console.error('[Auto-Allocation] Unexpected error:', err);
    return {
      success: false,
      reason: 'error',
      message: `Erreur inattendue: ${err.message}`
    };
  }
}
